import { useEffect, useState } from "react";
import { AnimatePresence } from "motion/react";
import { Navigate, useNavigate } from "react-router-dom";
import { t } from "ttag";

import {
  AnswerCheckboxGroup,
  AnswerRadioGroup,
} from "@/app/components/legacy-components";
import { useCustomerIoContext } from "@/features/analytics/providers";
import { trackDqPageViewed } from "@/features/analytics/trackers/dq-page-viewed";
import { trackHealthDisqualified } from "@/features/analytics/trackers/health-disqualified";
import { useEligibilityQuestions } from "@/features/onboarding/hooks";
import { ONBOARDING_ROUTE } from "@/features/onboarding/router";
import {
  PartialDisqualificationModal,
  TreatmentDisqualification,
} from "@/features/onboarding/sections";
import { useOnboardingStore } from "@/features/onboarding/stores";
import { GLP1_CATEGORIES } from "@/features/products/models";
import { LogoLoader } from "@/shared/components";
import { FIELD_TYPE } from "@/shared/models/legacy-constants";
import { FadeOut, Heading, Layout, SectionHeader, Text } from "@/shared/ui";
import { calculateBmi } from "@/shared/utils";

export const EligibilityQuestions = () => {
  const [errorMessage, setErrorMessage] = useState("");

  const { analytics } = useCustomerIoContext();

  const navigate = useNavigate();

  const bmiInputs = useOnboardingStore((state) => state.bmiInputs);
  const selectedCategories = useOnboardingStore(
    (state) => state.selectedCategories,
  );
  const setSelectedCategories = useOnboardingStore(
    (state) => state.setSelectedCategories,
  );
  const onlyGlp1Selected = selectedCategories.every((category) =>
    GLP1_CATEGORIES.includes(category.slug),
  );
  const glp1CategorySelected = selectedCategories.find((category) =>
    GLP1_CATEGORIES.includes(category.slug),
  );

  const handleFullDisqualification = () => {
    if (!onlyGlp1Selected) {
      return;
    }

    trackHealthDisqualified({ customerIoAnalytics: analytics });
    navigate(ONBOARDING_ROUTE.ELIGIBILITY_QUESTIONS_DQ, { replace: true });
  };

  const {
    question,
    disqualified,
    isLoading,
    handleAnswers,
    handleGoBack,
    getAnswersForQuestion,
    setAnswersForQuestion,
  } = useEligibilityQuestions(bmiInputs && calculateBmi(bmiInputs), {
    onDisqualified: handleFullDisqualification,
  });

  useEffect(() => {
    trackDqPageViewed({ customerIoAnalytics: analytics });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const answers = question ? getAnswersForQuestion(question.id) : [];

  const handlePartialDisqualificationGoBack = () => {
    const updatedCategories = selectedCategories.filter(
      (category) => !GLP1_CATEGORIES.includes(category.slug),
    );
    setSelectedCategories(updatedCategories);
    navigate(ONBOARDING_ROUTE.CHOOSE_PLAN);
  };

  const handlePartialDisqualificationContinue = () => {
    const updatedCategories = selectedCategories.filter(
      (category) => !GLP1_CATEGORIES.includes(category.slug),
    );
    setSelectedCategories(updatedCategories);
    navigate(ONBOARDING_ROUTE.ACCOUNT_PERSONAL_DETAILS);
  };

  const answerOptions = question?.onboardingAnswers.map((answer) => ({
    label: answer.answer,
    value: answer.id,
    unique: answer.unique,
    disqualifier: answer.disqualifier,
  }));

  const handleBack = () => {
    navigate(ONBOARDING_ROUTE.BMI);
  };

  const handleFullDisqualificationGoBack = () => {
    navigate(ONBOARDING_ROUTE.ELIGIBILITY_QUESTIONS, { replace: true });
    handleGoBack();
  };

  const handleNext = () => {
    if (!answers.length) {
      return setErrorMessage(t`Please select at least one answer to continue`);
    }
    handleAnswers(answers);
    setErrorMessage("");
  };

  if (!bmiInputs) {
    return <Navigate to={ONBOARDING_ROUTE.BMI} />;
  }

  return (
    <>
      <Layout.Content>
        <LogoLoader show={isLoading} fullscreen />

        <AnimatePresence key={question?.id}>
          {question && !isLoading && answerOptions && (
            <>
              <FadeOut>
                <SectionHeader title={question.title} />
              </FadeOut>

              <form className="flex w-full flex-col gap-6">
                <div className="flex flex-col gap-3">
                  <Heading level={2} color="primary" align="center">
                    {t`Do any of these apply to you?`}
                  </Heading>

                  <Text size="large" align="center">
                    {t`Any selection other than "None of the below" means you're not eligible`}
                  </Text>
                </div>

                <FadeOut>
                  {question.type === FIELD_TYPE.CHECKBOX && (
                    <AnswerCheckboxGroup
                      id={`${question.id}-${question.type}`}
                      options={answerOptions}
                      value={answers}
                      onChange={(value) => {
                        setAnswersForQuestion(question.id, value);
                        setErrorMessage("");
                      }}
                      className="mf-masked mf-listen"
                    />
                  )}

                  {question.type === FIELD_TYPE.RADIO && (
                    <AnswerRadioGroup
                      id={`${question.id}-${question.type}`}
                      options={answerOptions}
                      value={answers[0]}
                      onValueChange={(value) => {
                        setAnswersForQuestion(question.id, [value]);
                        if (errorMessage) {
                          setErrorMessage("");
                        }
                      }}
                      className="mf-masked mf-listen w-full sm:mx-auto sm:max-w-xl sm:grid-cols-1 sm:gap-6 sm:place-self-center lg:grid-cols-1 xl:grid-cols-1"
                    />
                  )}
                </FadeOut>
              </form>
            </>
          )}
        </AnimatePresence>

        {glp1CategorySelected && (
          <PartialDisqualificationModal
            open={disqualified && !onlyGlp1Selected}
            categorySlug={glp1CategorySelected.slug}
            onGoBack={handlePartialDisqualificationGoBack}
            onContinue={handlePartialDisqualificationContinue}
          />
        )}

        <TreatmentDisqualification
          open={disqualified && onlyGlp1Selected}
          onGoBack={handleFullDisqualificationGoBack}
        />

        <div className="sticky -bottom-6 flex w-full flex-col gap-3 border-t border-primary-100 bg-white py-6 sm:-bottom-16 sm:pb-16">
          {errorMessage && (
            <Text align="center" color="error" as="p">
              {errorMessage}
            </Text>
          )}

          <div className="flex w-full flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
            <Layout.Cancel onClick={handleBack}>{t`Back`}</Layout.Cancel>

            <Layout.Action onClick={handleNext}>{t`Continue`}</Layout.Action>
          </div>
        </div>
      </Layout.Content>
    </>
  );
};
